import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { IntakePhoto } from '@/api/intakeApi';
import { PhotoViewer } from './PhotoViewer';

interface PhotoGalleryProps {
  photos: IntakePhoto[];
  onDelete?: (photoId: number) => void;
  deleting?: boolean;
}

export function PhotoGallery({ photos, onDelete, deleting }: PhotoGalleryProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [failedIds, setFailedIds] = useState<Set<number>>(new Set());

  if (photos.length === 0) return null;

  function handleDeleteClick(photoId: number) {
    if (!onDelete) return;
    if (deleteConfirmId !== photoId) {
      setDeleteConfirmId(photoId);
      return;
    }
    setDeleteConfirmId(null);
    setLightboxIdx(null);
    onDelete(photoId);
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photos.map((photo, i) => (
          <div key={photo.photoId} className="relative flex-shrink-0 group">
            <button
              type="button"
              onClick={() => { setDeleteConfirmId(null); setLightboxIdx(i); }}
              disabled={!photo.url || failedIds.has(photo.photoId)}
              className="focus:outline-none disabled:cursor-not-allowed"
            >
              {!photo.url || failedIds.has(photo.photoId) ? (
                <span className="h-20 w-20 flex items-center justify-center text-center text-[10px] leading-tight text-neutral-500 bg-neutral-100 rounded-lg border border-neutral-200 px-2">Photo unavailable</span>
              ) : (
                <img
                  src={photo.url}
                  alt={`Customer intake photo ${i + 1}`}
                  loading="lazy"
                  onError={() => setFailedIds((current) => new Set(current).add(photo.photoId))}
                  className="h-20 w-20 object-cover rounded-lg border border-neutral-200 hover:opacity-80 transition-opacity"
                />
              )}
            </button>
            {onDelete && (
              <button
                type="button"
                title={deleteConfirmId === photo.photoId ? 'Tap again to confirm delete' : 'Delete photo'}
                disabled={deleting}
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(photo.photoId); }}
                className={`absolute top-1 right-1 rounded-full p-0.5 transition-all ${
                  deleteConfirmId === photo.photoId
                    ? 'bg-red-600 text-white opacity-100'
                    : 'bg-black/40 text-white opacity-0 group-hover:opacity-100'
                }`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {lightboxIdx !== null && (
        <PhotoViewer
          photos={photos}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}
